import { sortBy } from "lodash-es";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { type Router } from "vue-router";

import { track } from "shared/boot/analytics";
import { $streemApiV1 } from "shared/boot/api";
import { streamTypes } from "shared/constants";
import features from "shared/services/features";
import { useStreamsStore } from "shared/stores/streams";
import { useUserStore } from "shared/stores/user";
import { type StreamGroup } from "shared/types";

type AddStreamGroupParams = Partial<
  Pick<StreamGroup, "organisation_team_id" | "position">
> &
  Pick<
    StreamGroup,
    "color" | "label" | "social_bookmarks_stream" | "stream_type"
  >;

interface SetCurrentMentionStreamGroupIndex {
  isGroupClicked?: boolean;
  router: Router;
  streamGroupIndex: number;
  streamGroups: StreamGroup[];
}

export interface ShareStreamGroupUser {
  access_level: string;
  can_edit: number;
  user_id: number;
}

export interface ShareStreamGroupParams {
  notify_user_by_email: boolean;
  notify_user_by_phone: boolean;
  shared_with_users: ShareStreamGroupUser[];
}

export interface ShareStreamGroup {
  id: number;
  params: ShareStreamGroupParams;
}

function sortGroups(groups: StreamGroup[], userId: number) {
  return sortBy(groups, [
    (group) => (group.locked ? 0 : 1),
    (group) => group.user_id !== userId,
    "position",
  ]);
}

const handleErrors = true;

export const useStreamGroupsStore = defineStore("streamGroups", () => {
  const streamGroups = ref<StreamGroup[]>([]);
  const currentMentionStreamGroupIndex = ref<number>(-1);
  const currentSocialStreamGroupIndex = ref<number>(-1);

  const traditionalStreamGroupsWithoutLock = computed<StreamGroup[]>(() =>
    traditionalStreamGroups().filter((streamGroup) => !streamGroup.locked)
  );

  const mentionStreamGroupsWithoutLocked = computed<StreamGroup[]>(() =>
    mentionStreamGroups("media").filter((streamGroup) => !streamGroup.locked)
  );

  const socialStreamGroupsWithoutLocked = computed<StreamGroup[]>(() =>
    socialStreamGroups("social").filter((streamGroup) => !streamGroup.locked)
  );

  const socialOwnedStreamGroupsWithoutLocked = computed<StreamGroup[]>(() =>
    socialOwnedStreamGroups("social").filter(
      (streamGroup) => !streamGroup.locked
    )
  );

  const traditionalBookmarkGroupsWithoutLocked = computed<StreamGroup[]>(() =>
    traditionalBookmarkGroups().filter((streamGroup) => !streamGroup.locked)
  );

  const socialBookmarkGroupsWithoutLocked = computed<StreamGroup[]>(() =>
    socialBookmarkGroups().filter((streamGroup) => !streamGroup.locked)
  );

  const nextMentionStreamGroupPosition = computed<number>(
    () =>
      (mentionStreamGroups().reduce(
        (acc, current) => Math.max(acc, current.position),
        0
      ) || 0) + 1
  );

  const nextSocialStreamGroupPosition = computed<number>(
    () =>
      (socialStreamGroups().reduce(
        (acc, current) => Math.max(acc, current.position),
        0
      ) || 0) + 1
  );

  const nextTraditionalBookmarkGroupPosition = computed<number>(
    () =>
      (traditionalBookmarkGroups().reduce(
        (acc, current) => Math.max(acc, current.position),
        0
      ) || 0) + 1
  );

  const nextSocialBookmarkGroupPosition = computed<number>(
    () =>
      (socialBookmarkGroups().reduce(
        (acc, current) => Math.max(acc, current.position),
        0
      ) || 0) + 1
  );

  const mediaTeamMentionStreamGroups = computed<StreamGroup[]>(() => {
    const currentOrganisationTeamId =
      useUserStore().preferences.selected_team_media;

    return streamGroups.value.filter((streamGroup) => {
      const teamIds = streamGroup.organisation_team_ids || [];

      return (
        streamGroup.stream_type === streamTypes.mentionStream &&
        teamIds.includes(currentOrganisationTeamId as number)
      );
    });
  });

  function setCurrentMentionStreamGroupIndex({
    isGroupClicked = false,
    router,
    streamGroupIndex,
    streamGroups: currentStreamGroups,
  }: SetCurrentMentionStreamGroupIndex) {
    currentMentionStreamGroupIndex.value = streamGroupIndex;
    // the user clicked within `TheMenu` as opposed to issuing a keyboard shortcut (←/→)
    if (isGroupClicked) return;

    router
      .push({
        name: "stream",
        params: {
          groupSlug: currentStreamGroups[streamGroupIndex].slug,
        },
      })
      .catch(() => {});
  }

  async function getStreamGroups(): Promise<void> {
    const response = await $streemApiV1.get<StreamGroup[]>("stream_groups", {
      handleErrors,
    });

    streamGroups.value = response.data;
  }

  async function addStreamGroup({ params }: { params: AddStreamGroupParams }) {
    track("Created Group");

    const response = await $streemApiV1.post("stream_groups", {
      handleErrors,
      params,
    });

    streamGroups.value = [...streamGroups.value, response.data];

    return response;
  }

  async function updateStreamGroup({
    id,
    params,
  }: {
    id: number;
    params: StreamGroup;
  }) {
    track("Updated Group");

    const response = await $streemApiV1.put(`stream_groups/${id}`, {
      handleErrors,
      params,
    });

    streamGroups.value = streamGroups.value.map((streamGroup) =>
      streamGroup.id === id ? response.data : streamGroup
    );

    return response;
  }

  async function shareStreamGroup({ id, params }: ShareStreamGroup) {
    track("Stream Group Shared");

    const response = await $streemApiV1.put<StreamGroup>(
      `stream_groups/${id}/share`,
      {
        handleErrors,
        params,
      }
    );

    const streamGroup = response.data;

    streamGroups.value = streamGroups.value.map((group) =>
      group.id === id ? streamGroup : group
    );

    const groupStreamsLookup = Object.fromEntries(
      streamGroup.streams.map((stream) => [stream.id, stream])
    );

    // Update all included streams with the new 'shared' status
    const streamsStore = useStreamsStore();

    streamsStore.streams.forEach((stream) => {
      if (groupStreamsLookup[stream.id]) {
        streamsStore.setStream({
          data: {
            ...stream,
            shared: groupStreamsLookup[stream.id].shared,
          },
          id: stream.id,
        });
      }
    });
  }

  async function deleteStreamGroup({ id }: { id: number }) {
    track("Removed Group");

    await $streemApiV1.delete(`stream_groups/${id}`, { handleErrors });
    await Promise.all([getStreamGroups(), useStreamsStore().getStreams()]);
  }

  async function archiveStreamGroup({ id }: { id: number }) {
    track("Archived Group");

    await $streemApiV1.put(`stream_groups/${id}/archive`, { handleErrors });
    await Promise.all([getStreamGroups(), useStreamsStore().getStreams()]);
  }

  function previousMentionStreamGroupIndex({ router }: { router: Router }) {
    const filteredStreamGroups = mentionStreamGroups().filter(
      (group) => group.streams.length
    );

    const streamGroupIndex =
      currentMentionStreamGroupIndex.value === 0
        ? 0
        : currentMentionStreamGroupIndex.value - 1;

    if (currentMentionStreamGroupIndex.value !== streamGroupIndex) {
      setCurrentMentionStreamGroupIndex({
        router,
        streamGroupIndex,
        streamGroups: filteredStreamGroups,
      });
    }
  }

  function nextMentionStreamGroupIndex({ router }: { router: Router }) {
    const filteredStreamGroups = mentionStreamGroups().filter(
      (group) => group.streams.length
    );

    const streamGroupIndex =
      currentMentionStreamGroupIndex.value >= filteredStreamGroups.length - 1
        ? filteredStreamGroups.length - 1
        : currentMentionStreamGroupIndex.value + 1;

    if (currentMentionStreamGroupIndex.value !== streamGroupIndex) {
      setCurrentMentionStreamGroupIndex({
        router,
        streamGroupIndex,
        streamGroups: filteredStreamGroups,
      });
    }
  }

  function setCurrentSocialStreamGroupIndex({
    streamGroupIndex,
  }: {
    streamGroupIndex: number;
  }) {
    currentSocialStreamGroupIndex.value = streamGroupIndex;
  }

  function previousSocialStreamGroupIndex() {
    const streamGroupIndex =
      currentSocialStreamGroupIndex.value === 0
        ? 0
        : currentSocialStreamGroupIndex.value - 1;

    if (currentSocialStreamGroupIndex.value !== streamGroupIndex) {
      setCurrentSocialStreamGroupIndex({
        streamGroupIndex,
      });
    }
  }

  function nextSocialStreamGroupIndex() {
    const filteredStreamGroups = socialStreamGroups().filter(
      (group) => group.streams.length
    );

    const streamGroupIndex =
      currentSocialStreamGroupIndex.value >= filteredStreamGroups.length - 1
        ? filteredStreamGroups.length - 1
        : currentSocialStreamGroupIndex.value + 1;

    if (currentSocialStreamGroupIndex.value !== streamGroupIndex) {
      setCurrentSocialStreamGroupIndex({
        streamGroupIndex,
      });
    }
  }

  function getStreamGroupsByStreamType(
    type: number,
    product: string = "media"
  ): StreamGroup[] {
    if (!features.has("has_team_workspaces")) {
      return streamGroups.value.filter(
        (streamGroup) => streamGroup.stream_type === type
      );
    }

    const currentOrganisationTeamId =
      useUserStore().preferences[`selected_team_${product}`];

    return streamGroups.value.filter((streamGroup) => {
      if (streamGroup.stream_type !== type) return false;

      if (
        Array.isArray(streamGroup.organisation_team_ids) &&
        streamGroup.organisation_team_ids.length > 0
      ) {
        // If organisation_team_ids is non-empty, match array
        return streamGroup.organisation_team_ids.includes(
          currentOrganisationTeamId as number
        );
      }

      if (streamGroup.organisation_team_id) {
        // Otherwise, if organisation_team_id is set, match it
        return streamGroup.organisation_team_id === currentOrganisationTeamId;
      }

      // If both ids are null/empty, treat as global/unassigned
      return true;
    });
  }

  function mentionStreamGroups(product: string = "media"): StreamGroup[] {
    const userId = useUserStore().currentUser.id;

    const sortedBriefGroups = sortGroups(
      getStreamGroupsByStreamType(streamTypes.organisationBrief, product),
      userId
    );

    return [
      ...sortedBriefGroups,
      ...traditionalStreamGroups(product),
      ...traditionalBookmarkGroups(),
    ];
  }

  function traditionalBookmarkGroups(): StreamGroup[] {
    const filteredStreamGroups = getStreamGroupsByStreamType(
      streamTypes.bookmarkStream,
      "media"
    );

    const filteredGroups = filteredStreamGroups.filter(
      (streamGroup) => !streamGroup.social_bookmarks_stream
    );

    return sortGroups(filteredGroups, useUserStore().currentUser.id);
  }

  function traditionalStreamGroups(product: string = "media"): StreamGroup[] {
    const userId = useUserStore().currentUser.id;

    return sortGroups(
      getStreamGroupsByStreamType(streamTypes.mentionStream, product),
      userId
    );
  }

  function socialBookmarkGroups(): StreamGroup[] {
    const filteredStreamGroups = getStreamGroupsByStreamType(
      streamTypes.bookmarkStream,
      "social"
    );

    const filteredGroups = filteredStreamGroups.filter(
      (streamGroup) => streamGroup.social_bookmarks_stream === true
    );

    return sortGroups(filteredGroups, useUserStore().currentUser.id);
  }

  function socialStreamGroups(product: string = "social"): StreamGroup[] {
    return [...socialOwnedStreamGroups(product), ...socialBookmarkGroups()];
  }

  function socialOwnedStreamGroups(product = "social"): StreamGroup[] {
    const userId = useUserStore().currentUser.id;

    return sortGroups(
      getStreamGroupsByStreamType(streamTypes.socialStream, product),
      userId
    );
  }

  function getStreamGroupBySlug(slug: string): StreamGroup | undefined {
    return streamGroups.value.find((streamGroup) => streamGroup.slug === slug);
  }

  function getStreamGroupByGroupId(groupId: number): StreamGroup | undefined {
    return streamGroups.value.find((streamGroup) => streamGroup.id === groupId);
  }

  return {
    addStreamGroup,
    archiveStreamGroup,
    currentMentionStreamGroupIndex,

    currentSocialStreamGroupIndex,
    deleteStreamGroup,
    getStreamGroupByGroupId,
    getStreamGroupBySlug,
    getStreamGroups,
    getStreamGroupsByStreamType,
    mediaTeamMentionStreamGroups,
    mentionStreamGroups,
    mentionStreamGroupsWithoutLocked,
    nextMentionStreamGroupIndex,
    nextMentionStreamGroupPosition,

    nextSocialBookmarkGroupPosition,
    nextSocialStreamGroupIndex,
    nextSocialStreamGroupPosition,
    nextTraditionalBookmarkGroupPosition,
    previousMentionStreamGroupIndex,
    previousSocialStreamGroupIndex,
    setCurrentMentionStreamGroupIndex,
    setCurrentSocialStreamGroupIndex,
    shareStreamGroup,
    socialBookmarkGroups,
    socialBookmarkGroupsWithoutLocked,
    socialOwnedStreamGroups,
    socialOwnedStreamGroupsWithoutLocked,
    socialStreamGroups,
    socialStreamGroupsWithoutLocked,
    streamGroups,
    traditionalBookmarkGroups,
    traditionalBookmarkGroupsWithoutLocked,
    traditionalStreamGroups,
    traditionalStreamGroupsWithoutLock,
    updateStreamGroup,
  };
});

export default useStreamGroupsStore;
